<template>
  <div class="page-root dark" tabindex="0">
    <div class="layout">
      <!-- Sidebar -->
      <aside class="sidebar" :class="{ collapsed: mobileMenuOpen }">
        <div class="sidebar-inner">
          <v-img src="../assets/ali-bayani.jpg" class="profile-pic" ></v-img>

          <h1 class="sidebar-sub">Computer Engineer</h1>

          <nav class="socials" aria-label="Social links">
            <a :href="links.github" target="_blank" rel="noopener">GitHub</a>
            <a :href="links.scholar" target="_blank" rel="noopener">Google Scholar</a>
            <a :href="links.orcid" target="_blank" rel="noopener">ORCID</a>
            <a :href="links.linkedin" target="_blank" rel="noopener">LinkedIn</a>
            <a :href="links.researchgate" target="_blank" rel="noopener">ResearchGate</a>
            <a :href="links.kaggle" target="_blank" rel="noopener">Kaggle</a>
            <a :href="links.scopus" target="_blank" rel="noopener">Scopus</a>
            <a :href="'mailto:' + contact.email">Email</a>
          </nav>

          <div class="mini-stats" aria-hidden="true">
            <div><strong>{{ metrics.citations }}</strong><span>Citations</span></div>
            <div><strong>{{ metrics.hindex }}</strong><span>h-index</span></div>
            <div><strong>{{ metrics.i10index }}</strong><span>i10-index</span></div>
          </div>

          <button class="mobile-toggle" @click="mobileMenuOpen = !mobileMenuOpen" aria-expanded="mobileMenuOpen">
            {{ mobileMenuOpen ? 'Close' : 'Menu' }}
          </button>
        </div>
      </aside>

      <!-- Main content -->
      <main class="content">
        <!-- Intro -->
        <section class="card intro reveal">
          <div class="intro-grid">
            <div>
              <h2>Hello!</h2>
              <p class="lead">
                I have a B.Sc. in computer engineering, and I conduct research in AI, machine learning, and computer science, collaborating on interdisciplinary projects.
              </p>
            </div>
          </div>
        </section>

        <!-- News -->
        <section class="card reveal">
          <h3 class="section-title">News & Highlights</h3>
          <ul class="news">
            <li v-for="(n, i) in news" :key="i" class="news-item">
              <time class="news-date">{{ n.date }}</time>
              <span v-html="n.text"></span>
            </li>
          </ul>
        </section>

        <!-- Publications -->
        <section class="card reveal">
          <div class="section-heading">
            <h3 class="section-title">Publications</h3>
            <div class="section-actions">
              <button
                  class="link-btn"
                  style="color: #D4AF37;"
                  @click="showAll = !showAll"
              >
                {{ showAll ? 'Show Selected' : 'Show All' }}
              </button>
            </div>
          </div>

          <ol class="publications">
            <li v-for="(p, idx) in visiblePublications" :key="idx" class="publication">
              <div class="pub-meta">
                <div class="pub-title">{{ p.title }}</div>
                <div class="pub-authors">{{ p.author }}</div>
                <div class="pub-venue">
                  <span v-if="p.journal">{{ p.journal }}</span>
                  <span v-else-if="p.booktitle">{{ p.booktitle }}</span>
                  <span v-if="p.year"> - {{ p.year }}</span>
                </div>
              </div>

              <div class="pub-links">
                <a v-if="p.url" :href="p.url" target="_blank" rel="noopener">Link</a>
                <a v-if="p.doi" :href="formatDOI(p.doi)" target="_blank" rel="noopener">DOI</a>
                <a v-if="p.pdf" :href="p.pdf" target="_blank" rel="noopener">PDF</a>
              </div>
            </li>
          </ol>

          <p v-if="publications.length === 0" class="muted">
            No publications loaded. (You can edit the `publications` array in the component.)
          </p>
        </section>

        <!-- Projects -->
        <section class="card reveal">
          <h3 class="section-title">Projects</h3>
          <ul class="projects">
            <li v-for="(pr, i) in projects" :key="i">
              <strong>{{ pr.title }}</strong> - <span>{{ pr.desc }}</span>
              <a v-if="pr.link" :href="pr.link" target="_blank" rel="noopener" class="proj-link" style="color: #D4AF37;">[repo]</a>
            </li>
          </ul>
        </section>

        <!-- Awards / Teaching / Peer review -->
        <section class="grid-3">
          <div class="card reveal">
            <h4>Honors & Awards</h4>
            <ul>
              <li v-for="(a, i) in awards" :key="i">{{ a }}</li>
            </ul>
          </div>

          <div class="card reveal">
            <h4>Teaching</h4>
            <ul>
              <li v-for="(t, i) in teaching" :key="i">{{ t }}</li>
            </ul>
          </div>

          <div class="card reveal">
            <h4>Peer Review</h4>
            <ul>
              <li v-for="(r, i) in peerReview" :key="i">{{ r }}</li>
            </ul>
          </div>
        </section>

        <!-- Contact -->
        <section class="card reveal contact-card">
          <h3 class="section-title">Contact</h3>
          <p>Email: <a :href="'mailto:' + contact.email">{{ contact.email }}</a></p>
          <p>Affiliation: Robotics and Soft Technologies Research Center, Azad University of Tabriz</p>

          <div class="contact-actions">
            <a :href="links.github" target="_blank" rel="noopener">GitHub</a>
            <a :href="links.scholar" target="_blank" rel="noopener">Google Scholar</a>
            <a :href="links.linkedin" target="_blank" rel="noopener">LinkedIn</a>
            <a :href="links.orcid" target="_blank" rel="noopener">ORCID</a>
          </div>
        </section>

        <footer class="footer">
          <small>Last updated: {{ lastUpdated }} • © {{ currentYear }} Ali Bayani</small>
        </footer>
      </main>
    </div>
  </div>
</template>

<script>
export default {
  name: "ResumePage",
  data() {
    return {
      mobileMenuOpen: false,
      profileSrc: "/img/profile.jpg",
      contact: { email: "aliwbayani@gmail.com" },
      links: {
        github: "https://github.com/aliebayani",
        scholar: "https://scholar.google.com/citations?user=bACdbPYAAAAJ&hl=en",
        orcid: "https://orcid.org/0009-0003-6770-0546",
        linkedin: "https://www.linkedin.com/in/ali-bayani-a13505209",
        researchgate: "https://www.researchgate.net/profile/Ali-Bayani-2",
        kaggle: "https://www.kaggle.com/alibyni",
        scopus: "https://www.scopus.com/authid/detail.uri?authorId=59216051700"
      },

      metrics: {
        citations: 32,
        hindex: 3,
        i10index: 1
      },

      news: [
        { date: "Dec 2025", text: "Achieved an h-index of 3" },
        { date: "Dec 2025", text: "Completed 40+ peer reviews" },
        { date: "Apr 2025", text: "Awarded the <em>Professor Kambiz Badie Award</em> for best AI research at ICWR 2025" },
        { date: "Dec 2024", text: "Featured paper at IST 2024 (IEEE)" },
        { date: "Nov 2024", text: "Nominated for the <em>Wiley Research Hero Prize</em>" }
      ],

      publications: [
        // Journal Articles
        {
          title: "LDCNN: A new arrhythmia detection technique with ECG signals using a linear deep convolutional neural network",
          author: "Bayani, A.; Kargar, M.",
          journal: "Physiological Reports",
          year: "2024",
          doi: "10.14814/phy2.16182",
          url: "https://doi.org/10.14814/phy2.16182"
        },
        {
          title: "The Role of Artificial Intelligence and Machine Learning in Human Disease Diagnosis: A Comprehensive Review",
          author: "Imani, F.; Bayani, A.; Kargar, M.; Assadzadeh, A.",
          journal: "Iran Journal of Computer Science",
          year: "2025",
          doi: "10.1007/s42044-025-00321-0",
          url: "https://doi.org/10.1007/s42044-025-00321-0"
        },
        {
          title: "Adaptive Portfolio Optimization with Multi-Agent Deep Reinforcement Learning and Short-Term Performance Analysis",
          author: "Movassaghpour, S.; Kargar, M.; Bayani, A.; Assadzadeh, A.; Khakzadi, A.",
          journal: "International Journal of ICT Research (IEEE Iran Section)",
          year: "2025",
          url: "https://ijict.itrc.ac.ir/article-1-738-en.pdf"
        },
        {
          title: "HNMD-CNN: A Hierarchical Narrowing Multi-Deep Convolutional Neural Network for Precision Glioma Classification in 3D MRI Images",
          author: "Hoseini, N.S.; Kargar, M.; Bayani, A.; Ardabili, S.",
          journal: "Computational and Systems Oncology",
          year: "2025",
          doi: "10.1002/cso2.70002",
          url: "https://doi.org/10.1002/cso2.70002"
        },
        {
          title: "Impact of Data Selection Strategies on EEG-Based Epilepsy Detection Using Stochastic Data Reduction and the EEG Continuity Principle",
          author: "Hoseini, N.S.; Kargar, M.; Izadkhah, H.; Ahmadi, H.; Bayani, A.; Shokati A.",
          journal: "Under Review (Engineering Reports)",
          year: "2025",
          doi: "10.22541/au.176419314.42726046/v1",
          url: "https://doi.org/10.22541/au.176419314.42726046/v1"
        },

        // Conference Articles
        {
          title: "Integrating CNN-LSTM Networks with Statistical Filtering Techniques for Intelligent IoT Intrusion Detection",
          author: "Imani, F.; Kargar, M.; Assadzadeh, A.; Bayani, A.",
          booktitle: "Proc. of 8th Int. Conf. on Smart Cities, IoT and Applications (SCIoT)",
          year: "2024",
          doi: "10.1109/SCIoT62588.2024.10570107",
          url: "https://doi.org/10.1109/SCIoT62588.2024.10570107"
        },
        {
          title: "A Consulting System for Portfolio Assets Allocation by Selecting the Best Agent in the Short Term Based on Cumulative Returns with Deep Reinforcement Learning",
          author: "Movassaghpour, S.; Kargar, M.; Bayani, A.; Assadzadeh, A.; Khakzadi, A.",
          booktitle: "Proc. of 11th Int. Symposium on Telecommunications (IST)",
          year: "2024",
          doi: "10.1109/IST64061.2024.10843551",
          url: "https://doi.org/10.1109/IST64061.2024.10843551"
        },
        {
          title: "RBMD: RoBERTa-Based Module Detection in Multi-Programming Language Software Systems",
          author: "Kargar, M.; Movassaghpour, S.; Bayani, A.",
          booktitle: "Proc. of 11th Int. Conf. on Web Research (ICWR)",
          year: "2025",
          doi: "10.1109/ICWR65219.2025.11006198",
          url: "https://doi.org/10.1109/ICWR65219.2025.11006198"
        },
        // Industry / Professional Publications
        {
          title: "TransGAN-DX: A Hybrid Transformer-GAN Approach for Enhanced Cardiovascular Disease Diagnosis",
          author: "Bayani, A.; Kargar, M.; Kargar, P.; Samadian, E.",
          journal: "American Hospital & Healthcare Management, Research Insights",
          year: "2025",
          url: "https://www.americanhhm.com/researchinsights/transgan-dx-a-hybrid-transformer-gan-approach-for-enhanced-cardiovascular-disease-diagnosis"
        },
        {
          title: "AI-Driven Innovations in Cardiovascular Diagnostics and Remote Care",
          author: "Bayani, A.; Kargar, M.; Samadian, E.",
          journal: "Asian Hospital & Healthcare Management, Issue 67, pp. 27-31",
          year: "2025",
          url: "https://www.asianhhm.com/archives-magazine/60",
          pdf: "https://drive.google.com/file/d/1CDbYsHcJkw_hp7wLQM5SVBZKFsvppFkj/view?usp=drive_link"
        }
      ],

      projects: [
        {
          title: "Unified AI-Aware Scheduler",
          desc: "Auto-Tuned Scheduler Prototype for Heterogeneous GPU Clusters. (2025)",
          link: "https://github.com/aliebayani/Unified-AI-Aware-Scheduler"
        },
        {
          title: "HAT-ECG",
          desc: "Hybrid Autoencoder-Transformer for ECG arrhythmia classification. (2025)",
          link: "https://github.com/MasoudKargar/HAT-ECG"
        },
        {
          title: "RBMD",
          desc: "RoBERTa-Based Module Detection in Multi-Language Software Systems. (2025)",
          link: "https://github.com/MasoudKargar/RBMD"
        },
        {
          title: "TransGAN-DX",
          desc: "Transformer-GAN pipeline for cardiovascular disease diagnosis. (2025)",
          link: "https://github.com/aliebayani/TransGAN-DX"
        },
        {
          title: "EEG Epilepsy Detection",
          desc: "EEG epilepsy detection via data selection strategies. (2025)",
          link: "https://github.com/MasoudKargar/Data-Selection-EEG"
        },
        {
          title: "Portfolio Optimization with Deep RL",
          desc: "Portfolio optimization using deep reinforcement learning. (2024–25)",
          link: "https://github.com/MasoudKargar/APO-MADRL-STA"
        },
        {
          title: "HNMD-CNN",
          desc: "Hierarchical deep CNN for glioma classification in 3D MRI. (2024–25)",
          link: "https://github.com/MasoudKargar/HNMD-CNN"
        },
        {
          title: "IoT Intrusion Detection",
          desc: "CNN-LSTM with statistical filtering for IoT intrusion detection. (2024)",
          link: "https://github.com/aliebayani/IoT-Devices-Intrusion-Detection"
        },
        {
          title: "LDCNN ECG Arrhythmia Detection",
          desc: "ECG-based arrhythmia detection using Linear Deep CNN. (2023)",
          link: "https://github.com/aliebayani/Linear-Deep-Convolutional-Neural-Network-LDCNN"
        },
        {
          title: "DPANet / GAN & Seq2Seq",
          desc: "Protein secondary structure prediction via LSTM, Seq2Seq & GAN. (2024)"
        },
        {
          title: "Real-Time Heart Arrhythmia Diagnosis",
          desc: "Real-time heart arrhythmia detection via DRL & ECG. (2024)"
        },
        {
          title: "Disease Detection Using Deep & Machine Learning",
          desc: "Machine learning and deep learning for disease detection. (2024–25)"
        },
        {
          title: "Solubility Prediction via GAN",
          desc: "Solubility prediction in ASD formulations using GAN. (2024)"
        },
        {
          title: "Quantum Control of XY Spin Chain",
          desc: "Quantum control of XY spin chain with deep reinforcement learning. (2024)"
        },
        {
          title: "Graph Theory for Software Architecture Modeling",
          desc: "Graph-theory-based software architecture modeling. (2023)"
        },
        {
          title: "QA System with Transformer-Based RC",
          desc: "Question Answering system using transformer-based reading comprehension. (2023)"
        },
        {
          title: "Social Media Text Clustering",
          desc: "Social media text clustering via Liquid Neural Networks. (2023)"
        },
        {
          title: "SocialMedia-Django",
          desc: "Lightweight quote-sharing platform (Internet Engineering Course Project)",
          link: "https://github.com/aliebayani/SocialMedia-Django"
        },
        {
          title: "E-CRM Recommender System",
          desc: "Recommender system for e-CRM. (2022)"
        },
        {
          title: "Retinal Disease Classification",
          desc: "Retinal disease classification using deep learning. (2021)"
        }
      ],

      awards: [
        "Professor Kambiz Badie Award - ICWR 2025",
        "Wiley Research Hero Nominee - 2024",
        "Featured Paper - IST 2024",
        "Second-highest GPA in Department - Azad University of Tabriz"
      ],

      teaching: [
        "Teaching Assistant - Artificial Intelligence & Expert Systems (Fall 2022)",
        "Teaching Assistant - Special Topics I: Social Networks & Complex Networks (Fall 2022)"
      ],

      peerReview: [
        "Engineering Applications of Artificial Intelligence - 18 reviews",
        "IEEE Transactions on Artificial Intelligence - 3 reviews",
        "Computers & Electrical Engineering - 11 reviews",
        "Expert Systems with Applications - 2 reviews",
        "IEEE Access - 3 review",
        "Iran Journal of Computer Science - 11 reviews",
        "Case Reports and Series in Medicine - 1 review"
      ],

      showAll: false
    };
  },
  computed: {
    visiblePublications() {
      return this.showAll ? this.publications : this.publications.slice(0, 6);
    },
    lastUpdated() {
      return "January 2026";
    },
    currentYear() {
      return new Date().getFullYear();
    }
  },
  mounted() {
    // Simple scroll-reveal
    const reveals = this.$el.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
        entries => {
          entries.forEach(e => {
            if (e.isIntersecting) {
              e.target.classList.add("in-view");
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.12 }
    );
    reveals.forEach(r => io.observe(r));
  },
  methods: {
    formatDOI(doi) {
      if (!doi) return "";
      if (doi.startsWith("http")) return doi;
      return "https://doi.org/" + doi.replace(/^doi:\s*/i, "");
    }
  }
};
</script>

<style scoped>
/* Dark-mode only styles */
:root {
  --bg: #0b1220;
  --card: #071022;
  --muted: #9aa4b2;
  --text: #e6eef6;
  --accent: #06b6d4;
  --glass: rgba(255, 255, 255, 0.03);
  --shadow: 0 6px 20px rgba(2, 6, 23, 0.6);
}

.page-root {
  background: linear-gradient(180deg, var(--bg), var(--bg));
  color: var(--text);
  min-height: 100vh;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
}

/* Topbar */
.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  background: transparent;
  position: sticky;
  top: 0;
  z-index: 20;
}
.topbar .brand {
  display: flex;
  gap: 12px;
  align-items: center;
}
.logo {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
  box-shadow: var(--shadow);
  border: 1px solid rgba(255, 255, 255, 0.04);
}
.name {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}
.tagline {
  margin: 0;
  font-size: 0.9rem;
  color: var(--muted);
}

/* Layout */
.layout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 28px;
  max-width: 1200px;
  margin: 28px auto;
  padding: 0 18px;
}
.sidebar {
  background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01));
  border-radius: 10px;
  padding: 18px;
  box-shadow: var(--shadow);
  height: fit-content;
  transition: transform .25s ease, opacity .25s ease;
}
.sidebar-inner { display: flex; flex-direction: column; gap: 12px; align-items: start; }
.profile { width: 120px; height: 120px; border-radius: 8px; object-fit: cover; }
.sidebar-name { margin: 6px 0 0; font-weight: 700; font-size: 1.05rem; }
.sidebar-sub { margin: 0; color: var(--muted); font-size: 0.92rem; }

/* socials */
.socials { display:flex; flex-direction: column; gap: 8px; margin-top: 8px; width:100%; }
.socials a { color: var(--text); text-decoration: none; font-size: 0.95rem; }
.socials a:hover { color: #D4AF37; text-decoration: underline; }

/* mini stats */
.mini-stats { display:flex; gap: 12px; margin-top: 12px; width:100%; justify-content: space-between; }
.mini-stats div { text-align:center; font-size: 0.85rem; color: var(--muted); }
.mini-stats strong { display:block; font-size:1.05rem; color:var(--text); }

/* mobile toggle */
.mobile-toggle { display:none; margin-top:4px; background:transparent; border:1px solid rgba(255,255,255,0.04); padding:6px 8px; border-radius:6px; cursor:pointer; color:var(--text); }

/* Content */
.content { padding-bottom: 40px; }
.card { background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01)); border-radius: 10px; padding: 18px; margin-bottom: 18px; box-shadow: var(--shadow); transition: transform .28s cubic-bezier(.2,.9,.3,1), opacity .28s; opacity: 0; transform: translateY(12px) scale(.995); }
.card.in-view { opacity: 1; transform: translateY(0) scale(1); }

.section-title { margin: 0 0 8px; font-size:1.05rem; font-weight:700; }
.lead { margin: 8px 0 0; color: var(--muted); line-height:1.6; }
.intro-grid { display:flex; justify-content:space-between; gap: 12px; align-items: center; }
.quick-links { display:flex; gap:8px; flex-wrap:wrap; }
.chip { padding:8px 10px; border-radius: 999px; background: var(--glass); color: var(--text); text-decoration:none; font-size:0.9rem; border:1px solid rgba(255,255,255,0.03); }

/* news */
.news { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:8px; }
.news-item { display:flex; gap:12px; align-items:flex-start; color:var(--muted); }
.news-date { font-weight:700; color:var(--accent); min-width:84px; }

/* tags */
.tags { display:flex; gap:8px; flex-wrap:wrap; }
.tag { padding:8px 10px; border-radius:999px; background:var(--glass); font-size:0.88rem; color:var(--text); }

/* publications */
.publications { list-style: none; padding-left: 0; margin: 0; display:flex; flex-direction:column; gap:12px; }
.publication { display:flex; justify-content:space-between; gap:8px; align-items:flex-start; }
.pub-title { font-weight:600; color:var(--text); }
.pub-authors { color: var(--muted); font-size:0.9rem; }
.pub-links a { margin-left: 10px; color: var(--accent); text-decoration:none; }
.pub-links a:hover { text-decoration: underline; }

/* projects */
.projects { list-style: none; padding-left: 0; margin: 0; display:flex; flex-direction:column; gap:8px; }
.proj-link { margin-left: 8px; font-size:0.9rem; color:var(--accent); }

/* grid-3 */
.grid-3 { display:grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.grid-3 .card { margin-bottom: 0; }

/* contact */
.contact-card p { margin: 6px 0; color:var(--muted); }
.contact-actions a { margin-right: 12px; color:var(--accent); }

/* footer */
.footer { text-align:center; margin-top: 18px; color: var(--muted); }

/* modal placeholder removed (no BibTeX) */

/* reveal animation handled via class in-view */

/* ========== Responsive ========== */
@media (max-width: 980px) {
  .layout { grid-template-columns: 1fr; padding: 0 14px; gap: 12px; }
  .sidebar { order: 2; display:block; }
  .mobile-toggle { display:block; }
  .topbar { padding: 12px; }
  .logo { width:48px; height:48px; }
  .name { font-size:1rem; }
  .grid-3 { grid-template-columns: 1fr; }
}

a {
  color: var(--accent);
}

a:hover {
  color: #D4AF37;
}

</style>
